const BLOB_URL_TTL_MS = 10 * 60 * 1000;
const PDF_MIME_TYPE = 'application/pdf';

const scheduleBlobUrlCleanup = (blobUrl: string, delay = BLOB_URL_TTL_MS) => {
  window.setTimeout(() => {
    URL.revokeObjectURL(blobUrl);
  }, delay);
};

const asBlobWithType = (blob: Blob, type: string) =>
  blob.type === type ? blob : new Blob([blob], { type });

const ensureFileExtension = (fileName: string, extension: string) => {
  const normalizedFileName = fileName.trim() || `documento.${extension}`;
  return normalizedFileName.toLowerCase().endsWith(`.${extension}`)
    ? normalizedFileName
    : `${normalizedFileName}.${extension}`;
};

const isMobileBrowser = () =>
  /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const openBlobInNewTab = (blobUrl: string) => {
  const openedWindow = window.open(blobUrl, '_blank');

  if (!openedWindow) {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  }
};

export const downloadBlob = (blob: Blob, fileName: string, cleanupDelay = 1000) => {
  if (!blob) return;

  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = blobUrl;
  link.download = fileName;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  link.remove();

  scheduleBlobUrlCleanup(blobUrl, cleanupDelay);
};

export const downloadPdfBlob = async (blob: Blob, fileName: string) => {
  if (!blob) return;

  const pdfBlob = asBlobWithType(blob, PDF_MIME_TYPE);
  const finalFileName = ensureFileExtension(fileName, 'pdf');

  if (isMobileBrowser()) {
    const file = new File([pdfBlob], finalFileName, {
      type: PDF_MIME_TYPE,
    });

    try {
      if (
        navigator.share &&
        navigator.canShare?.({
          files: [file],
        })
      ) {
        await navigator.share({
          files: [file],
          title: finalFileName,
        });

        return;
      }
    } catch (error) {
      console.error('No se pudo compartir el PDF:', error);
    }
  }

  downloadBlob(pdfBlob, finalFileName, isMobileBrowser() ? BLOB_URL_TTL_MS : 1000);
};

export async function printBlob(blob: Blob) {
  if (!blob) return false;

  const blobUrl = URL.createObjectURL(asBlobWithType(blob, PDF_MIME_TYPE));

  if (isMobileBrowser()) {
    // On mobile, open the PDF in a new tab so the user can preview it
    // (and optionally download/print from the browser's built-in controls).
    openBlobInNewTab(blobUrl);
    scheduleBlobUrlCleanup(blobUrl, BLOB_URL_TTL_MS);
    return true;
  }

  if (navigator.pdfViewerEnabled) {
    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.setAttribute('aria-hidden', 'true');

      let cleaned = false;
      let settled = false;

      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;

        iframe.remove();
        URL.revokeObjectURL(blobUrl);
      };

      const fallbackCleanup = window.setTimeout(() => {
        cleanup();
      }, BLOB_URL_TTL_MS);

      iframe.onload = () => {
        try {
          const printWindow = iframe.contentWindow;

          if (!printWindow) {
            throw new Error('Print iframe window not available');
          }

          const handleAfterPrint = () => {
            window.setTimeout(() => {
              window.clearTimeout(fallbackCleanup);
              cleanup();
            }, 2000);
          };

          printWindow.addEventListener('afterprint', handleAfterPrint, { once: true });

          window.setTimeout(() => {
            try {
              printWindow.focus();
              printWindow.print();

              if (!settled) {
                settled = true;
                resolve(true);
              }
            } catch (error) {
              window.clearTimeout(fallbackCleanup);
              cleanup();

              const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);

              if (!settled) {
                settled = true;
                reject(new Error(`Printing failed: ${errorMessage}`));
              }
            }
          }, 300);
        } catch (error) {
          window.clearTimeout(fallbackCleanup);
          cleanup();

          const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);

          if (!settled) {
            settled = true;
            reject(new Error(`Printing failed: ${errorMessage}`));
          }
        }
      };

      iframe.onerror = (err) => {
        window.clearTimeout(fallbackCleanup);
        cleanup();

        const errorMessage = err instanceof Error ? err.message : JSON.stringify(err);

        if (!settled) {
          settled = true;
          reject(new Error(`Printing failed: ${errorMessage}`));
        }
      };

      iframe.src = blobUrl;
      document.body.appendChild(iframe);
    });
  }

  openBlobInNewTab(blobUrl);
  scheduleBlobUrlCleanup(blobUrl, BLOB_URL_TTL_MS);
  return true;
}
